import { campaignCopyField, type CampaignCopyFieldId } from './fields.js';

// FRANCHISE CAMPAIGN COPY — ⚠️⚠️ INSERT IS EXPLICIT, PER-FIELD, AND ADDITIVE.
//
// Two rules, and both are about not destroying work:
//
//   1. IT NEVER OVERWRITES SILENTLY. A campaign's authored copy is somebody's
//      afternoon. An "Insert" that quietly replaced entry 3 would be discovered
//      later, by which point the original is gone. So: APPEND while there is
//      room; at capacity, name exactly which entry would go and make the AM pick
//      it. "Show what will be replaced before replacing it" is the requirement,
//      and requiring a deliberate pick satisfies it strictly.
//
//   2. GOOGLE HEADLINES ARE STRICTLY ADDITIVE, AND THE INHERITED BRAND
//      BOILERPLATE IS NEVER TOUCHED. Brand headlines are seeded onto a campaign
//      at create (server/modules/campaigns/service.ts) and thereafter live in the
//      same array as the AM's own — so "never modify an inherited headline" is
//      guaranteed by never modifying ANY existing headline, which is a stricter
//      rule and needs no matching heuristic to enforce.
//
// ⚠️ THE 15 IS A TOTAL AND EVERYTHING IN THE ARRAY COUNTS. Capacity is
// `15 − whatever is already there`, inherited or authored: it is one array with
// one cap in the zod schema and one cap at Google. If it is already full we say
// so plainly rather than generating options that cannot be used.

export interface InsertPlan {
  /** The array as it would be after the insert. */
  next: string[];
  /** True when the entry was added without displacing anything. */
  appended: boolean;
  /** The entry this would replace, when the AM picked one. */
  replaced: string | null;
  /**
   * Set when the field is full and no replacement was chosen. The UI renders the
   * existing entries so the AM can pick one; nothing is written until they do.
   */
  atCapacity: boolean;
  /** How many more entries this field can hold, before this insert. */
  remaining: number;
}

/**
 * What one insert would do. PURE — the caller decides whether to apply it, and
 * the workspace shows the plan before anything is written.
 *
 * @param replaceIndex the entry the AM chose to replace. Only honoured when the
 *   field is at capacity: while there is room, appending is always the right
 *   answer and an accidental index must not destroy an entry.
 */
export function planCampaignCopyInsert(
  fieldId: CampaignCopyFieldId,
  current: string[],
  text: string,
  replaceIndex?: number,
): InsertPlan {
  const field = campaignCopyField(fieldId);
  // Blank entries are what the campaign form seeds a new campaign with
  // (`bodyCopy: ['']`), so they are free slots rather than content — counting
  // them as occupied would report a brand-new campaign as full.
  const filled = current.filter((v) => v.trim() !== '');
  const remaining = Math.max(0, field.maxEntries - filled.length);

  if (remaining > 0) {
    // ⚠️ Reuse the first blank slot rather than growing the array past its cap:
    // the seeded [''] must not become ['', 'the new copy'] and read as two.
    const blank = current.findIndex((v) => v.trim() === '');
    const next =
      blank === -1
        ? [...current, text]
        : current.map((v, i) => (i === blank ? text : v));
    return { next, appended: true, replaced: null, atCapacity: false, remaining };
  }

  if (replaceIndex === undefined || replaceIndex < 0 || replaceIndex >= current.length) {
    // Full, and nobody has said what to drop. Write nothing.
    return { next: current, appended: false, replaced: null, atCapacity: true, remaining: 0 };
  }

  return {
    next: current.map((v, i) => (i === replaceIndex ? text : v)),
    appended: false,
    replaced: current[replaceIndex] ?? null,
    atCapacity: true,
    remaining: 0,
  };
}

/**
 * How many NEW entries the generator may propose for a field.
 *
 * ⚠️ Zero is a real answer and must be SAID, not silently produce nothing: an AM
 * who clicks Generate on a full field and gets an empty panel reads it as broken.
 */
export function campaignCopyCapacity(fieldId: CampaignCopyFieldId, current: string[]): number {
  const field = campaignCopyField(fieldId);
  const filled = current.filter((v) => v.trim() !== '').length;
  return Math.max(0, field.maxEntries - filled);
}

/**
 * The message shown when a field is already full. Named per field so "Google
 * headlines" and "Meta descriptions" do not share one vague sentence.
 *
 * ⚠️ IT NAMES BOTH WAYS OUT, because the workspace offers both: remove one and
 * insert, or replace one deliberately. An earlier wording said only "remove one
 * to add another", which the Replace control on screen immediately contradicted
 * — and a message contradicted by the button next to it teaches an AM to stop
 * reading messages.
 */
export function capacityMessage(fieldId: CampaignCopyFieldId, current: string[]): string | null {
  const field = campaignCopyField(fieldId);
  if (campaignCopyCapacity(fieldId, current) > 0) return null;
  return `All ${field.maxEntries} ${field.pluralLabel} are already filled — inserting will replace one you choose.`;
}
